import { createSelector } from 'reselect'
import { List, OrderedMap } from 'immutable'

import { getCurrentUser } from 'data/users/selectors'
import { getOrganization } from 'data/organizations/selectors'
import { getActiveChannelId } from 'data/ui/selectors'
import { getAllUserGroups } from 'data/userGroups/selectors'

import ChannelsAPI from 'remote/channels'
import { PERSONAL } from 'remote/channels/types'
import { canAccessChannel } from 'remote/channels/roles'

import { isPublic } from './record'
import { isInvited, isArchived } from 'data/users/record'
import { getEntityMap, getEntity } from 'data'

export const getChannels = createSelector(
  state => getEntityMap(state, 'Channel'),
  allChannels =>
    allChannels.filter(channel => {
      return channel.get('id').startsWith('templates-') === false
    })
)

export const getAccessibleChannels = createSelector(getChannels, allChannels =>
  allChannels.filter(channel => {
    return canAccessChannel({ channel })
  })
)

export const getChannel = (state, channelId) =>
  getEntity(state, 'Channel', channelId)

export const getAdminChannels = createSelector(
  state => getOrganization(state).get('adminChannels'),
  getChannels,
  (adminChannels, allChannels) => {
    return adminChannels
      .map(userChannel => allChannels.get(userChannel.get('id')))
      .filter(channel => Boolean(channel) && channel.get('type') !== PERSONAL)
  }
)

export const getChannelsList = createSelector(getChannels, channels => {
  return channels.toList()
})

function getAllChannelMembership(state) {
  return getEntityMap(state, 'ChannelMembership')
}

function getAllUsers(state) {
  return getEntityMap(state, 'User')
}

export function getChannelMembershipConnection(state, channelId) {
  return getEntity(state, 'ChannelMembershipConnection', channelId)
}

export const getFirstChannelId = createSelector(getChannels, allChannels => {
  const firstChannel = allChannels
    .sort((a, b) => a.get('name').localeCompare(b.get('name')))
    .first()
  return firstChannel ? firstChannel.get('id') : null
})

export function createChannelAdminCountSelector() {
  return createSelector(
    getAllChannelMembership,
    getChannelMembershipConnection,
    function getChannelAdminCount(
      allChannelMemberships,
      channelMembershipConnection
    ) {
      if (!channelMembershipConnection) {
        return null
      }

      return channelMembershipConnection
        .get('nodes')
        .map(node => allChannelMemberships.get(node.id))
        .filter(membership => membership.get('role') === 'admin').size
    }
  )
}

export const getChannelMembersWithSubjects = createSelector(
  getAllChannelMembership,
  getChannelMembershipConnection,
  getAllUsers,
  getAllUserGroups,
  (
    allChannelMemberships,
    channelMembershipConnection,
    allUsers,
    allUserGroups
  ) => {
    if (!channelMembershipConnection) {
      return OrderedMap()
    }

    return channelMembershipConnection.get('nodes').map(node => {
      const membership = allChannelMemberships.get(node.id)
      const subject = membership.get('subject')
      let subjectValue
      switch (subject.__typename) {
        case 'User':
          subjectValue = allUsers.get(subject.id)
          break
        case 'UserGroup':
          subjectValue = allUserGroups.get(subject.id)
          break
        default:
          throw new Error(
            `Unknown or missing subject type ${subject.__typename}`
          )
      }
      const membershipWithSubject = membership.set('subject', subjectValue)
      return membershipWithSubject
    })
  }
)

function getAllUserGroupsUserConnections(state) {
  return getEntityMap(state, 'UserConnection')
}

export const listUsersWithAccessToChannel = createSelector(
  getChannel,
  getChannelMembershipConnection,
  getAllChannelMembership,
  getAllUsers,
  getAllUserGroups,
  getAllUserGroupsUserConnections,
  (
    channel,
    channelMembershipConnection,
    allChannelMemberships,
    allUsers,
    allUserGroups,
    allUserGroupsUserConnections
  ) => {
    if (isPublic(channel)) {
      return allUsers.toList()
    }
    if (!channelMembershipConnection) {
      return List()
    }
    return channelMembershipConnection
      .get('nodes')
      .reduce((channelUsers, node) => {
        const membership = allChannelMemberships.get(node.id)
        const subject = membership.get('subject')
        switch (subject.__typename) {
          case 'User':
            const user = allUsers.get(subject.id)
            return channelUsers.push(user)
          case 'UserGroup':
            const userGroup = allUserGroups.get(subject.id)
            const userConnection = allUserGroupsUserConnections.get(
              userGroup.id
            )
            const users = userConnection.nodes.map(({ id }) => allUsers.get(id))
            return channelUsers.concat(users)
          default:
            throw new Error(
              `Unknown or missing subject type ${subject.__typename}`
            )
        }
      }, List())
  }
)

export const getChannelUsersMemberships = createSelector(
  getChannelMembersWithSubjects,
  function getChannelUsersMemberships(memberships = OrderedMap()) {
    return memberships
      .filter(membership => {
        const user = membership.get('subject')

        return user && user.username && !isArchived(user)
      })
      .map(({ subject, role }) => ({ user: subject, role }))
  }
)

export const getChannelUserGroupsMemberships = createSelector(
  getChannelMembersWithSubjects,
  function getChannelUserGroupsMemberships(memberships = OrderedMap()) {
    return memberships
      .filter(membership => {
        const user = membership.get('subject')

        return user && user.name && !isInvited(user) && !isArchived(user)
      })
      .map(({ subject, role }) => ({ userGroup: subject, role }))
  }
)

export const getPersonalChannel = state => {
  const viewer = getCurrentUser(state)
  const channelId = ChannelsAPI.getPersonalChannelId(viewer.id)
  return getEntity(state, 'Channel', channelId)
}

export const getActiveChannel = state => {
  const activeChannelId = getActiveChannelId(state)
  return getChannel(state, activeChannelId)
}

export function listSettingsToListName(connectionSettings) {
  const { channelId, orderBy } = connectionSettings
  return `channel:${channelId}::${orderBy.field}:${orderBy.direction}`
}
